/**
 * Settings → AI's pure pieces (slice 1c spec §6.1): the provider cards and
 * their chips, each account's state, usage and actions, and model routing's
 * choices, edits and checks (§4.1). The components only render these.
 */
import type { StatusTone } from "@crew/ui/components/status";
import type { AiConnection, AiProvider, ConnectionUsage, Me } from "../../app/me";
import {
  defaultUnserved,
  groupOf,
  modelChangeError,
  modelGroup,
  modelLabel,
  PROVIDERS,
  signsIn,
  type Catalog,
  type CatalogModel,
} from "../setup/onboarding";

type Ai = NonNullable<Me["computer"]>["ai"];

/** The default model and the fallbacks, in order: what `PUT /api/computer/ai/routing` takes. */
export type Routing = { model: string | null; fallbacks: string[] };

/** At most this many fallbacks (spec §11, choice 8). */
export const MAX_FALLBACKS = 5;

/** The launch providers, in the order the Add a provider grid shows them. */
const PROVIDER_ORDER = Object.keys(PROVIDERS) as AiProvider[];

/** Where each provider shows an account's usage (spec §1: each card links to it). */
const USAGE_PAGES: Record<Exclude<AiProvider, "workers-ai">, string> = {
  chatgpt: "https://chatgpt.com/codex/settings/usage",
  openai: "https://platform.openai.com/usage",
  openrouter: "https://openrouter.ai/activity",
  // OpenClaw reads no usage for an Anthropic key (slice 1k spec §5): the Console has it.
  anthropic: "https://console.anthropic.com/settings/usage",
  // xAI reports no usage for a Grok sign-in (slice 1f spec §5): this is all there is.
  grok: "https://grok.com",
  // Crew reads no usage for a Runware key (slice 1o spec §1): its dashboard has it.
  runware: "https://my.runware.ai",
  // Nor does a Claude setup token (slice 1g spec §6).
  claude: "https://claude.ai/settings/usage",
};

/** An account with a credential on the computer: working, or refused and waiting to be reconnected. */
function credentialed(c: AiConnection): boolean {
  return c.status === "connected" || c.status === "needs_reconnect";
}

const connections = (ai: Ai): AiConnection[] => ai.connections ?? [];

/** Accounts in the order OpenClaw tries them (a copy: `/api/me`'s list stays as it is). */
const byPosition = (list: AiConnection[]): AiConnection[] =>
  [...list].sort((a, b) => a.position - b.position);

/** The OpenClaw providers routing may use (§4.1): those with an account that has a credential. */
export function usableGroups(ai: Ai): Set<string> {
  return new Set(
    connections(ai)
      .filter(credentialed)
      .map((c) => groupOf(c.provider)),
  );
}

/** What a provider is used for (§6.1): the default model's, and which fallbacks (1-based). */
export type Roles = { default: boolean; fallbacks: number[] };

export function rolesOf(provider: AiProvider, routing: Routing): Roles {
  const group = groupOf(provider);
  const fallbacks: number[] = [];
  routing.fallbacks.forEach((f, i) => {
    if (modelGroup(f) === group) fallbacks.push(i + 1);
  });
  return { default: routing.model !== null && modelGroup(routing.model) === group, fallbacks };
}

/** The chips a provider card shows: "Default", "Fallback 1 · Fallback 2". */
export function roleChips(roles: Roles): string[] {
  const chips = roles.default ? ["Default"] : [];
  if (roles.fallbacks.length > 0) {
    chips.push(roles.fallbacks.map((n) => `Fallback ${n}`).join(" · "));
  }
  return chips;
}

/** When an account was added: `created_at`, else when it connected, else never (last). */
function addedAt(c: AiConnection): number {
  const at = c.created_at ?? c.connected_at;
  return at ? Date.parse(at) : Number.POSITIVE_INFINITY;
}

/**
 * When a provider was first connected: its earliest account. Not the
 * accounts' positions, so reordering accounts never moves the cards.
 */
function firstAdded(ai: Ai, provider: AiProvider): number {
  const times = connections(ai)
    .filter((c) => c.provider === provider)
    .map(addedAt);
  return Math.min(...times);
}

export type ProviderCard = { provider: AiProvider; accounts: AiConnection[]; roles: Roles };

/**
 * One card per provider with an account (§6.1), in the order they were
 * connected (each provider's first appearance in `/api/me`'s list), its
 * accounts in the order OpenClaw tries them.
 */
export function providerCards(ai: Ai): ProviderCard[] {
  const routing = routingOf(ai);
  const order = PROVIDER_ORDER.filter((p) => connections(ai).some((c) => c.provider === p));
  // Stable: providers that tie (no times yet) keep the launch order.
  const at = new Map(order.map((p) => [p, firstAdded(ai, p)]));
  order.sort((a, b) => (at.get(a) === at.get(b) ? 0 : at.get(a)! - at.get(b)!));
  return order.map((provider) => ({
    provider,
    accounts: byPosition(connections(ai).filter((c) => c.provider === provider)),
    roles: rolesOf(provider, routing),
  }));
}

/**
 * The provider whose route keeps `provider` out (slice 1k spec §3): Anthropic
 * keys and a Claude subscription share OpenClaw's `anthropic` order, so while
 * one has an account that isn't failed, the other can't be connected. A pi
 * computer has both (ADR-0020).
 */
function heldAnthropicRoute(ai: Ai, provider: AiProvider): AiProvider | null {
  if (ai.anthropic_both) return null;
  const other = provider === "claude" ? "anthropic" : provider === "anthropic" ? "claude" : null;
  if (!other) return null;
  return connections(ai).some((c) => c.provider === other && c.status !== "failed") ? other : null;
}

/** The card Reconnect in a thread opens at (§6.3): the default model's provider. */
export function defaultCard(ai: Ai): AiProvider | null {
  const cards = providerCards(ai);
  if (ai.provider && cards.some((c) => c.provider === ai.provider)) return ai.provider;
  return cards.find((c) => c.roles.default)?.provider ?? null;
}

/** The workspace's routing as `/api/me` has it. */
export function routingOf(ai: Ai): Routing {
  return { model: ai.model, fallbacks: ai.fallbacks ?? [] };
}

function timeFormat(timezone: string, withDay: boolean): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
    ...(withDay ? { month: "short", day: "numeric" } : {}),
  });
}

const dayOf = (at: number, timezone: string) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(at);

/** "3:40 PM" today, else "Sep 28, 3:40 PM", in the workspace's timezone. */
function clockTime(at: string, now: number, timezone: string): string {
  const t = Date.parse(at);
  return timeFormat(timezone, dayOf(t, timezone) !== dayOf(now, timezone)).format(t);
}

/** The health states a read can leave an account in (§5.1), past the ones its status says. */
const unwell = new Set(["resting", "out_of_credit", "unavailable", "signed_out"]);

/** Whether an account's last read says it's resting, out of credit or unavailable right now. */
function healthy(c: AiConnection, now: number): boolean {
  const health = c.health;
  if (!health || !unwell.has(health.state)) return true;
  // A rest or an out-of-credit spell that's over reads as working.
  const over = health.until !== undefined && Date.parse(health.until) <= now;
  return (health.state === "resting" || health.state === "out_of_credit") && over;
}

/**
 * The account OpenClaw uses for the default model (§3.3): the first working
 * one, by position, of the default's OpenClaw provider.
 */
export function inUseAccount(ai: Ai, now: number): string | null {
  if (!ai.model) return null;
  const group = modelGroup(ai.model);
  const first = byPosition(
    connections(ai).filter((c) => groupOf(c.provider) === group && c.status === "connected"),
  ).find((c) => healthy(c, now));
  return first?.id ?? null;
}

/** An account's state (§6.1): its key, the words, their tone, and why when it's a problem. */
export type AccountState = {
  key:
    | "connecting"
    | "failed"
    | "needs_reconnect"
    | "resting"
    | "out_of_credit"
    | "unavailable"
    | "in_use"
    | "connected";
  text: string;
  tone: StatusTone;
  reason?: string;
};

function until(text: string, at: string | undefined, now: number, timezone: string): string {
  return at ? `${text} until ${clockTime(at, now, timezone)}` : text;
}

/** A read that says the account can't answer right now (§5.1), or null. */
function healthState(c: AiConnection, now: number, timezone: string): AccountState | null {
  const health = c.health;
  if (!health || healthy(c, now)) return null;
  const reason = c.error ? { reason: c.error } : {};
  switch (health.state) {
    case "signed_out":
      return { key: "needs_reconnect", text: "Needs reconnecting", tone: "error", ...reason };
    case "resting":
      return {
        key: "resting",
        text: until("Resting", health.until, now, timezone),
        tone: "waiting",
      };
    case "out_of_credit": {
      const text = until("Out of credit", health.until, now, timezone);
      // Workers AI's spent free allocation (slice 1h spec §7): what lifts it.
      const why =
        health.code === "free_allocation"
          ? { reason: "Out of Workers AI's free daily allocation. Workers Paid lifts the limit." }
          : {};
      return { key: "out_of_credit", text, tone: "error", ...why };
    }
    default:
      return { key: "unavailable", text: "Unavailable", tone: "waiting", ...reason };
  }
}

/**
 * How an account reads: Connecting…, Didn't connect, Needs reconnecting
 * (and why), what its last read says (Resting until …, Out of credit,
 * Unavailable), else In use for the one the default is served by, or
 * Connected.
 */
export function accountState(c: AiConnection, ai: Ai, now: number, timezone: string): AccountState {
  const reason = c.error ? { reason: c.error } : {};
  if (c.status === "connecting") return { key: "connecting", text: "Connecting…", tone: "working" };
  if (c.status === "failed") {
    return { key: "failed", text: "Didn't connect", tone: "error", ...reason };
  }
  if (c.status === "needs_reconnect") {
    return { key: "needs_reconnect", text: "Needs reconnecting", tone: "error", ...reason };
  }
  const read = healthState(c, now, timezone);
  if (read) return read;
  return inUseAccount(ai, now) === c.id
    ? { key: "in_use", text: "In use", tone: "done" }
    : { key: "connected", text: "Connected", tone: "done" };
}

/** How long before a setup token runs out its card says so (slice 1g spec §6). */
const EXPIRY_NOTICE_MS = 30 * 86_400_000;

/**
 * A Claude setup token within 30 days of its year: "Token expires Sep 29,
 * 2027. Paste a new one before then." Null otherwise, or once it's past (the
 * account then needs reconnecting).
 */
export function expiryNote(c: AiConnection, now: number, timezone: string): string | null {
  const at = c.health?.expires_at;
  if (!at) return null;
  const left = Date.parse(at) - now;
  if (left > EXPIRY_NOTICE_MS || left <= 0) return null;
  const day = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: timezone,
  }).format(Date.parse(at));
  return `Token expires ${day}. Paste a new one before then.`;
}

/** Replace a working credential, Reconnect one that isn't. */
export function replaceLabel(state: AccountState): "Replace" | "Reconnect" {
  return state.key === "needs_reconnect" || state.key === "failed" ? "Reconnect" : "Replace";
}

/**
 * The workspace's only account, on an Anthropic route (slice 1k spec §3.3):
 * its Disconnect is the way to the other route, and leaves no AI until then.
 */
export function switchesRoute(c: AiConnection, ai: Ai): boolean {
  return (c.provider === "anthropic" || c.provider === "claude") && connections(ai).length === 1;
}

/**
 * Why Disconnect is disabled, or null (§7's `409 in_use`): the account is
 * the last one with a credential behind the default model. The workspace's
 * only account on an Anthropic route may go (slice 1k spec §3.3): it's the
 * way to the other route.
 */
export function disconnectBlock(c: AiConnection, ai: Ai): string | null {
  if (!ai.model || !credentialed(c)) return null;
  if (switchesRoute(c, ai)) return null;
  const group = groupOf(c.provider);
  if (group !== modelGroup(ai.model)) return null;
  const behind = connections(ai).filter((o) => credentialed(o) && groupOf(o.provider) === group);
  return behind.length === 1
    ? "This is the last account behind your default model. Make another model the default first."
    : null;
}

/**
 * The account's up and down arrows (§3.3): shown when its OpenClaw provider
 * has more than one account, since they're tried in one order.
 */
export function orderMoves(c: AiConnection, ai: Ai): { show: boolean; up: boolean; down: boolean } {
  const group = byPosition(
    connections(ai).filter((o) => groupOf(o.provider) === groupOf(c.provider)),
  );
  const at = group.findIndex((o) => o.id === c.id);
  return { show: group.length > 1, up: at > 0, down: at >= 0 && at < group.length - 1 };
}

/** "5h" is "5-hour limit", "Week" "Weekly limit". */
export function windowLabel(label: string): string {
  const hours = /^(\d+)\s*h$/i.exec(label);
  if (hours) return `${hours[1]}-hour limit`;
  const days = /^(\d+)\s*d$/i.exec(label);
  if (days) return days[1] === "7" ? "Weekly limit" : `${days[1]}-day limit`;
  const named: Record<string, string> = {
    day: "Daily limit",
    week: "Weekly limit",
    month: "Monthly limit",
  };
  return named[label.toLowerCase()] ?? `${label} limit`;
}

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;

/** "resets in 3 hours", "resets in 20 minutes", "resets in 2 days". */
export function resetsIn(at: string, now: number): string {
  const minutes = Math.round((Date.parse(at) - now) / 60_000);
  if (minutes <= 0) return "resets now";
  if (minutes < 60) return `resets in ${plural(minutes, "minute")}`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `resets in ${plural(hours, "hour")}`;
  return `resets in ${plural(Math.round(hours / 24), "day")}`;
}

export type UsageWindow = { label: string; pct: number; text: string };

export type UsageView = {
  plan: string | null;
  windows: UsageWindow[];
  summary: string | null;
  asOf: string;
};

/** An account's usage as the card shows it (§5.2): the plan, a line per window, the balance. */
export function usageView(usage: ConnectionUsage, now: number, timezone: string): UsageView {
  const plan = usage.plan ? `${usage.plan[0].toUpperCase()}${usage.plan.slice(1)} plan` : null;
  return {
    plan,
    windows: usage.windows.map((w) => {
      const pct = Math.min(100, Math.max(0, Math.round(w.used_pct)));
      const resets = w.resets_at ? ` · ${resetsIn(w.resets_at, now)}` : "";
      return { label: windowLabel(w.label), pct, text: `${pct}% used${resets}` };
    }),
    summary: usage.summary ?? null,
    asOf: `as of ${clockTime(usage.read_at, now, timezone)}`,
  };
}

/**
 * The card's usage link (spec §1): the provider's page, or for Workers AI,
 * the account's own page in Cloudflare's dashboard (slice 1h spec §6).
 */
export function usageLink(
  provider: AiProvider,
  accounts: AiConnection[],
): { href: string; text: string } {
  if (provider === "workers-ai") {
    const id = accounts[0]?.account_id;
    return {
      href: id ? `https://dash.cloudflare.com/${id}/ai/workers-ai` : "https://dash.cloudflare.com",
      text: "See usage in the Cloudflare dashboard",
    };
  }
  if (provider === "anthropic") {
    return { href: USAGE_PAGES.anthropic, text: "See usage in the Anthropic Console" };
  }
  return {
    href: USAGE_PAGES[provider],
    text: `See usage on the ${PROVIDERS[provider].name} website`,
  };
}

/** Providers with one account at a time: a new credential is a Replace (slice 1g §1, slice 1h §3.3). */
export function oneAccount(provider: AiProvider): boolean {
  return provider === "claude" || provider === "workers-ai";
}

/** A heading of models: who serves them (and their plan), and the provider whose mark it shows. */
export type ModelOptionGroup = { label: string; models: CatalogModel[]; mark?: AiProvider };

/** The plan an OpenClaw provider's accounts are on, as their usage reads it: "Max plan". */
function planIn(ai: Ai, group: string): string | null {
  const plan = connections(ai).find((c) => groupOf(c.provider) === group && c.usage?.plan)?.usage
    ?.plan;
  return plan ? `${plan[0].toUpperCase()}${plan.slice(1)} plan` : null;
}

/** The Crew providers with a credential in an OpenClaw provider, by name: "ChatGPT and OpenAI". */
export function namesIn(ai: Ai, group: string): string {
  const names = PROVIDER_ORDER.filter(
    (p) => groupOf(p) === group && connections(ai).some((c) => c.provider === p && credentialed(c)),
  ).map((p) => PROVIDERS[p].name);
  return names.join(" and ");
}

/**
 * The models routing can pick from (§4.1, §6.1): the connected providers'
 * catalogs, grouped by provider, then current and previous generation. A
 * ChatGPT sign-in and an OpenAI key share one list.
 */
export function modelOptions(catalog: Catalog | undefined, ai: Ai): ModelOptionGroup[] {
  if (!catalog) return [];
  const groups: ModelOptionGroup[] = [];
  const seen = new Set<string>();
  const usable = usableGroups(ai);
  for (const provider of PROVIDER_ORDER) {
    const group = groupOf(provider);
    if (!usable.has(group) || seen.has(group)) continue;
    seen.add(group);
    const name = namesIn(ai, group);
    const plan = planIn(ai, group);
    // The mark of the account that serves them: ChatGPT's or OpenAI's for `openai/*`.
    const mark = PROVIDER_ORDER.find(
      (p) =>
        groupOf(p) === group && connections(ai).some((c) => c.provider === p && credentialed(c)),
    );
    const models = catalog[provider] ?? [];
    const current = models.filter((m) => m.generation === "current");
    const last = models.filter((m) => m.generation === "last");
    const label = plan ? `${name} · ${plan}` : name;
    if (current.length > 0) groups.push({ label, models: current, mark });
    if (last.length > 0)
      groups.push({ label: `${name} · previous generation`, models: last, mark });
  }
  return groups;
}

/** "via ChatGPT": which of the owner's providers serves a model. */
export function viaText(model: string, ai: Ai): string | null {
  // Nothing serves it: the notice above says so, and no provider is named.
  if (model === ai.model && defaultUnserved(ai)) return null;
  const names = namesIn(ai, modelGroup(model));
  return names ? `via ${names}` : null;
}

/** The models Add a fallback model… offers: any the routing doesn't already use. */
export function fallbackChoices(options: ModelOptionGroup[], routing: Routing): ModelOptionGroup[] {
  const used = new Set([routing.model, ...routing.fallbacks]);
  return options
    .map((g) => ({ ...g, models: g.models.filter((m) => !used.has(m.id)) }))
    .filter((g) => g.models.length > 0);
}

/** A fallback one place up or down. */
export function moveFallback(routing: Routing, index: number, direction: "up" | "down"): Routing {
  const to = direction === "up" ? index - 1 : index + 1;
  if (to < 0 || to >= routing.fallbacks.length) return routing;
  const fallbacks = [...routing.fallbacks];
  [fallbacks[index], fallbacks[to]] = [fallbacks[to], fallbacks[index]];
  return { ...routing, fallbacks };
}

export function removeFallback(routing: Routing, index: number): Routing {
  return { ...routing, fallbacks: routing.fallbacks.filter((_, i) => i !== index) };
}

export function addFallback(routing: Routing, model: string): Routing {
  return { ...routing, fallbacks: [...routing.fallbacks, model] };
}

/** Make default: the fallback becomes the default, and the old default takes its place. */
export function makeDefault(routing: Routing, index: number): Routing {
  const model = routing.fallbacks[index];
  if (model === undefined) return routing;
  const fallbacks = routing.fallbacks.flatMap((f, i) =>
    i !== index ? [f] : routing.model ? [routing.model] : [],
  );
  return { model, fallbacks };
}

/** A new default from the select: out of the fallbacks if it was one. */
export function setDefault(routing: Routing, model: string): Routing {
  return { model, fallbacks: routing.fallbacks.filter((f) => f !== model) };
}

/**
 * §4.1 before it's sent: a default, every model from a connected provider,
 * no duplicate fallbacks, never the default as one, at most five. The same
 * words as the server's `invalid_routing` and `invalid_model`.
 */
export function routingProblem(routing: Routing, ai: Ai, catalog: Catalog | undefined) {
  if (!routing.model) return "Pick a default model.";
  const { model, fallbacks } = routing;
  if (fallbacks.length > MAX_FALLBACKS) return "Up to five fallback models.";
  if (new Set(fallbacks).size !== fallbacks.length) return "A model can be a fallback only once.";
  if (fallbacks.includes(model)) return "The default model can't also be a fallback.";
  // What's saved may stay with no account behind it, as the server allows
  // (§4.1): the default as the default, a saved model as a fallback.
  const usable = usableGroups(ai);
  const kept = new Set([ai.model, ...(ai.fallbacks ?? [])]);
  const served = (m: string) => usable.has(modelGroup(m));
  const stranded =
    model !== ai.model && !served(model)
      ? model
      : fallbacks.find((f) => !kept.has(f) && !served(f));
  return stranded ? `${modelLabel(catalog, stranded)} needs a connected account.` : null;
}

/**
 * Where a connect in progress shows, when this page didn't start it (a
 * reload): the card of the account that's connecting, or ChatGPT's while a
 * sign-in with no account yet (slice 1b's) waits.
 */
export function connectingProvider(ai: Ai): AiProvider | null {
  if (ai.status !== "connecting") return null;
  const account = connections(ai).find((c) => c.status === "connecting");
  if (account) return account.provider;
  return ai.pending ? "chatgpt" : null;
}

/**
 * The workspace's own state above the cards, when asks are affected (§3.2):
 * no connected account serves the default while another works (a connect
 * never moves it), nothing serves it (`failed`), or every account behind it
 * needs reconnecting. Null while it works or a change runs.
 */
export function aiNotice(ai: Ai, catalog?: Catalog): string | null {
  if (defaultUnserved(ai)) {
    return `No connected account serves ${modelLabel(catalog, ai.model)}: choose a default.`;
  }
  const why = ai.error ? `: ${ai.error}` : "";
  if (ai.status === "failed") return `Didn't connect${why}`;
  if (ai.status === "needs_reconnect") return `Needs reconnecting${why}`;
  if (ai.status === "none") return "Not connected. Connect a provider below.";
  return null;
}

/** Whether the owner has a working account yet: until then, there's no routing to edit. */
export function hasCredential(ai: Ai): boolean {
  return connections(ai).some(credentialed);
}

/**
 * A routing select's items: every model offered, and the current one even if
 * its provider went or it left the catalog (by its id without the provider,
 * so the owner sees what they're on and can choose another).
 */
export function selectItems(
  groups: ModelOptionGroup[],
  catalog: Catalog | undefined,
  value: string | null,
): Array<{ value: string; label: string }> {
  const items = groups.flatMap((g) => g.models.map((m) => ({ value: m.id, label: m.label })));
  if (value === null || items.some((i) => i.value === value)) return items;
  const offered = !catalog || Object.values(catalog).some((ms) => ms.some((m) => m.id === value));
  const label = offered
    ? (modelLabel(catalog, value) ?? value)
    : `${value.split("/").at(-1)} (no longer offered)`;
  return [...items, { value, label }];
}

/**
 * What the routing card says under the list: a problem found before
 * sending, the server's refusal, or a change that didn't apply on the
 * computer (it was rolled back).
 */
export function routingNote(problem: string | null, refused: string | null, ai: Ai) {
  const failed = modelChangeError(ai);
  return problem ?? refused ?? (failed ? `The last change didn't apply: ${failed}` : null);
}

/** Saving… on the routing card: its own change, or an order or disconnect (a connect shows in its card). */
export function routingSaving(pending: boolean, ai: Ai): boolean {
  return pending || (ai.status === "connecting" && connectingProvider(ai) === null);
}

/** A fallback row: its name, who serves it, and whether it can move up or down. */
export function fallbackRow(routing: Routing, index: number, ai: Ai, catalog: Catalog | undefined) {
  const model = routing.fallbacks[index];
  return {
    name: modelLabel(catalog, model) ?? model,
    via: viaText(model, ai),
    up: index > 0,
    down: index < routing.fallbacks.length - 1,
  };
}

/**
 * Where the connect panel shows (§6.1): the card of the provider it's for,
 * or under Add a provider while that provider has no card yet. Opened here,
 * or, after a reload, the connect already running.
 */
function panelHome(opened: AiProvider | undefined, ai: Ai) {
  const provider = opened ?? connectingProvider(ai);
  const inCard = connections(ai).some((c) => c.provider === provider);
  return { provider, inCard };
}

/** The providers Crew can't read usage for (slice 1k §5, 1f §5, 1g §6, 1h §6, 1o §1): a link instead. */
const NO_USAGE = new Set<AiProvider>([
  "openai",
  "openrouter",
  "anthropic",
  "workers-ai",
  "runware",
]);

/**
 * A provider row's usage, closed: the fullest window of the account in use
 * (else the first with a read), as "5-hour" and its percent; the balance
 * when there's no window; or, with no read, the link to the provider's own
 * page, said to be all there is for an API key.
 */
export type RowUsage =
  | { kind: "bar"; label: string; pct: number }
  | { kind: "text"; text: string }
  | { kind: "link"; href: string; site: string; unshared: boolean };

/** The provider's site, as a usage link names it: "Cloudflare", "OpenRouter". */
function usageSite(provider: AiProvider): string {
  if (provider === "workers-ai") return "Cloudflare";
  return PROVIDERS[provider].name;
}

export function rowUsage(card: ProviderCard, ai: Ai, now: number): RowUsage {
  const inUse = inUseAccount(ai, now);
  const read =
    card.accounts.find((c) => c.id === inUse && c.usage)?.usage ??
    card.accounts.find((c) => c.usage)?.usage;
  const fullest = [...(read?.windows ?? [])].sort((a, b) => b.used_pct - a.used_pct)[0];
  if (fullest) {
    const pct = Math.min(100, Math.max(0, Math.round(fullest.used_pct)));
    return { kind: "bar", label: windowLabel(fullest.label).replace(/ limit$/, ""), pct };
  }
  if (read?.summary) return { kind: "text", text: read.summary };
  const link = usageLink(card.provider, card.accounts);
  return {
    kind: "link",
    href: link.href,
    site: usageSite(card.provider),
    unshared: NO_USAGE.has(card.provider),
  };
}

/** A provider row's line under its name: "Max plan · 1 account", "API key · 2 accounts". */
export function rowSubtitle(card: ProviderCard): string {
  const { provider, accounts } = card;
  const cloudflare = provider === "workers-ai" ? accounts[0]?.account_id : undefined;
  if (cloudflare) return `Cloudflare account ${cloudflare.slice(0, 4)}…${cloudflare.slice(-4)}`;
  const plan = accounts.find((c) => c.usage?.plan)?.usage?.plan;
  const how = plan ? `${plan[0].toUpperCase()}${plan.slice(1)} plan` : PROVIDERS[provider].connects;
  return `${how} · ${plural(accounts.length, "account")}`;
}

/** The order note inside an open row, when its accounts are tried in an order (slice 1j §3.9). */
export function orderNote(card: ProviderCard, ai: Ai): string | null {
  if (!card.accounts.some((c) => orderMoves(c, ai).show)) return null;
  return "Accounts are tried in this order. Moving one applies at once; it isn't part of Save.";
}

/** A provider row's chip: the tone the badge takes, and whether the row opens by itself. */
export type RowStatus = {
  text: string;
  tone: "done" | "working" | "waiting" | "neutral";
  open: boolean;
};

/**
 * What a closed row says of its accounts: Connecting…, a problem (its own
 * words for one account, "2 need you" for more), all resting or unavailable,
 * else In use or Connected. A row with a problem or a connect opens itself.
 */
export function rowStatus(card: ProviderCard, ai: Ai, now: number, timezone: string): RowStatus {
  const states = card.accounts.map((c) => accountState(c, ai, now, timezone));
  if (states.some((s) => s.key === "connecting")) {
    return { text: "Connecting…", tone: "working", open: true };
  }
  const problems = states.filter((s) => s.tone === "error");
  if (problems.length > 0) {
    const text =
      states.length === 1
        ? problems[0].text
        : `${problems.length} need${problems.length === 1 ? "s" : ""} you`;
    return { text, tone: "waiting", open: true };
  }
  if (states.length > 0 && states.every((s) => s.tone === "waiting")) {
    return { text: states[0].text, tone: "neutral", open: false };
  }
  return states.some((s) => s.key === "in_use")
    ? { text: "In use", tone: "done", open: false }
    : { text: "Connected", tone: "done", open: false };
}

/** A company Add a provider lists first (step 1), with its ways to connect (step 2). */
export type Maker = {
  id: "openai" | "anthropic" | "xai" | "openrouter" | "cloudflare" | "runware";
  name: string;
  about: string;
  ways: AiProvider[];
};

export const MAKERS: readonly Maker[] = [
  { id: "openai", name: "OpenAI", about: "GPT models", ways: ["chatgpt", "openai"] },
  { id: "anthropic", name: "Anthropic", about: "Claude models", ways: ["claude", "anthropic"] },
  { id: "xai", name: "xAI", about: "Grok", ways: ["grok"] },
  { id: "openrouter", name: "OpenRouter", about: "Many models, one key", ways: ["openrouter"] },
  {
    id: "cloudflare",
    name: "Cloudflare Workers AI",
    about: "Open models on Cloudflare",
    ways: ["workers-ai"],
  },
  { id: "runware", name: "Runware", about: "Open models, one key", ways: ["runware"] },
];

/** How a way is paid for: a plan the owner has, or a key billed per use. */
function wayKind(provider: AiProvider): "plan" | "key" {
  return provider === "chatgpt" || provider === "grok" || provider === "claude" ? "plan" : "key";
}

/** The tag a way shows in step 1: Plan, API key, API token. */
function wayTag(provider: AiProvider): string {
  if (wayKind(provider) === "plan") return "Plan";
  return provider === "workers-ai" ? "API token" : "API key";
}

/** A way's card in step 2: its tag, title and line, and why it can't be picked (or null). */
export type Way = {
  provider: AiProvider;
  tag: string;
  title: string;
  line: string;
  connected: number;
  blocked: string | null;
  /** Blocked by the other Anthropic route (slice 1k §3.2), not by an account of its own. */
  held: boolean;
};

const WAY_WORDS: Record<AiProvider, { title: string; line: string }> = {
  chatgpt: { title: "Sign in with ChatGPT", line: "Uses your ChatGPT plan's limits." },
  openai: { title: "OpenAI API key", line: "OpenAI bills you for what you use." },
  grok: { title: "Sign in with SuperGrok", line: "Uses your SuperGrok plan." },
  claude: { title: "Your Claude plan", line: "With a setup token from Claude Code." },
  anthropic: { title: "Anthropic API key", line: "Anthropic bills you for what you use." },
  openrouter: { title: "OpenRouter API key", line: "OpenRouter bills you for what you use." },
  "workers-ai": { title: "Workers AI API token", line: "Cloudflare bills you for what you use." },
  runware: { title: "Runware API key", line: "Runware bills you for what you use." },
};

/** Why a way can't be added now: the other Anthropic route holds it, or its one account is in. */
function wayBlock(ai: Ai, provider: AiProvider, connected: number): string | null {
  const held = heldAnthropicRoute(ai, provider);
  if (held === "claude") {
    return "Your Claude plan is connected. A workspace uses the plan or API keys, not both.";
  }
  if (held === "anthropic") {
    return "Your Anthropic API keys are connected. A workspace uses them or a Claude plan, not both.";
  }
  if (connected > 0 && oneAccount(provider)) {
    return "Connected. Replace it from its row under Providers.";
  }
  return null;
}

/** A maker's ways, as step 2 shows them: those not turned off, with what's connected and what's held. */
export function makerWays(maker: Maker, ai: Ai): Way[] {
  const off = new Set(ai.providers_off ?? []);
  return maker.ways
    .filter((p) => !off.has(p))
    .map((provider) => {
      const connected = connections(ai).filter((c) => c.provider === provider).length;
      return {
        provider,
        tag: wayKind(provider) === "plan" ? "Uses your plan" : "Pay per use",
        ...WAY_WORDS[provider],
        connected,
        blocked: wayBlock(ai, provider, connected),
        held: heldAnthropicRoute(ai, provider) !== null,
      };
    });
}

/**
 * A maker in step 1: its tags, what's connected (or, with no way left, why:
 * the other Anthropic route's words), and whether there's a way left to add.
 */
export type MakerRow = { maker: Maker; tags: string[]; note: string | null; open: boolean };

/** Step 1's list: every maker with a way not turned off; one with none left to add shows, closed. */
export function makerRows(ai: Ai): MakerRow[] {
  return MAKERS.flatMap((maker) => {
    const ways = makerWays(maker, ai);
    if (ways.length === 0) return [];
    const connected = ways.reduce((n, w) => n + w.connected, 0);
    const open = ways.some((w) => w.blocked === null);
    const held = open ? undefined : ways.find((w) => w.held)?.blocked;
    const note = held ?? (connected > 0 ? `${plural(connected, "account")} connected` : null);
    return [{ maker, tags: ways.map((w) => wayTag(w.provider)), note, open }];
  });
}

/** The way step 2 goes straight to: the only one it could pick, when that's all there is. */
export function onlyWay(ways: Way[]): AiProvider | null {
  return ways.length === 1 && ways[0].blocked === null ? ways[0].provider : null;
}

/** Add a provider's line under its title: step 1's ask, then how the maker's ways are paid. */
export function stepLine(maker: Maker | null, ways: Way[]): string {
  if (!maker) return "Pick who your crew should think with.";
  if (ways.length > 1) return "Use a plan you already pay for, or a key billed per use.";
  return ways[0]?.line ?? "";
}

/**
 * Where the connect panel shows (§6.1), from where it was opened: in Add a
 * provider's window (`window`, for that way); in a row (`row`, the provider
 * it's for, or after a reload the one whose connect is running); or under
 * the rows (`orphan`) for a sign-in that has no account yet: one from before
 * a reload, or one the window was closed on (its provider kept).
 */
export function slotPlaces(
  opened: { provider: AiProvider; where: "row" | "window" } | null,
  ai: Ai,
): { window: AiProvider | null; row: AiProvider | null; orphan: boolean } {
  if (opened?.where === "window") return { window: opened.provider, row: null, orphan: false };
  const home = panelHome(opened?.provider, ai);
  return {
    window: null,
    row: home.inCard ? home.provider : null,
    orphan: home.provider !== null && !home.inCard,
  };
}

/**
 * Whether Add a provider's window hands its connect to the provider's row:
 * once a key (or token) is accepted and its account has a row, the row
 * follows it to Connected and the window closes. A sign-in stays in the
 * window: its code shows where it was started.
 */
export function handsOff(
  opened: { provider: AiProvider; where: "row" | "window" } | null,
  accepted: boolean,
  ai: Ai,
): boolean {
  if (opened?.where !== "window" || !accepted || signsIn(opened.provider)) return false;
  return connections(ai).some((c) => c.provider === opened.provider);
}
